import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, Section } from '../styles/components';

const ContactHero = styled.section`
  position: relative;
  padding: 4rem 0;
  overflow: hidden;
  background: url('/images/about.webp') no-repeat center center;
  background-size: cover;
  color: white;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 6rem 0;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: black;
  opacity: 0.5;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 48rem;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;

  @media (min-width: 768px) {
    font-size: 3.75rem;
  }
`;

const HeroText = styled.p`
  font-size: 1.125rem;
  line-height: 1.75;
  max-width: 48rem;
  margin: 0 auto 2rem;
  color: white;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const SupportTag = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: var(--red-50);
  color: var(--primary);
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  max-width: 64rem;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContactForm = styled.form`
  background: var(--gray-50);
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.375rem;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.375rem;
  font-size: 1rem;
  min-height: 8rem;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: var(--primary);
  color: white;
  font-weight: 500;
  border-radius: 0.375rem;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: var(--primary-dark);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  background: var(--red-50);
  color: var(--primary);
  padding: 0.75rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const InfoText = styled.p`
  color: var(--gray-600);
  line-height: 1.5;
`;

const MapContainer = styled.div`
  margin-top: 4rem;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  aspect-ratio: 16/9;
`;

const MapTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
  text-align: center;
  margin: 4rem 0 2rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  background: var(--red-50);
  color: var(--primary);
  padding: 0.75rem;
  border-radius: 9999px;
  transition: all 0.2s;

  &:hover {
    background: var(--red-100);
  }
`;

const Icon = styled.svg`
  width: 1.5rem;
  height: 1.5rem;
  fill: none;
  stroke: currentColor;
`;

const SocialIcon = styled.svg`
  width: 1.25rem;
  height: 1.25rem;
  fill: currentColor;
`;

const LocationIcon = () => (
  <Icon viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </Icon>
);

const ClockIcon = () => (
  <Icon viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </Icon>
);

const PhoneIcon = () => (
  <Icon viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </Icon>
);

const FacebookIcon = () => (
  <SocialIcon viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
  </SocialIcon>
);

const InstagramIcon = () => (
  <SocialIcon viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
  </SocialIcon>
);

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div>
      <ContactHero>
        <HeroOverlay />
        <Container>
          <HeroContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <SupportTag>Contact Us</SupportTag>
              <HeroTitle>Get In Touch</HeroTitle>
              <HeroText>
                Have questions about our products, need to place a special order, or just want to share your Ota Tofu experience? We'd love to hear from you.
              </HeroText>
            </motion.div>
          </HeroContent>
        </Container>
      </ContactHero>

      <Section>
        <Container>
          <ContactGrid>
            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>Send Us a Message</FormTitle>
              <FormGroup>
                <Label htmlFor="name">Your Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="message">Your Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <SubmitButton type="submit">Send Message</SubmitButton>
            </ContactForm>

            <ContactInfo>
              <InfoItem>
                <IconWrapper>
                  <LocationIcon />
                </IconWrapper>
                <InfoContent>
                  <InfoTitle>Our Location</InfoTitle>
                  <InfoText>
                    812 SE Stark St<br />
                    Portland, OR 97214
                  </InfoText>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <IconWrapper>
                  <ClockIcon />
                </IconWrapper>
                <InfoContent>
                  <InfoTitle>Business Hours</InfoTitle>
                  <InfoText>
                    Monday to Friday: 8am - 5pm<br />
                    Saturday: 8am - 3pm<br />
                    Sunday: Closed
                  </InfoText>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <IconWrapper>
                  <PhoneIcon />
                </IconWrapper>
                <InfoContent>
                  <InfoTitle>Contact Details</InfoTitle>
                  <InfoText>
                    Phone: (503) 232-8947<br />
                    Email: info@otapdx.com
                  </InfoText>
                </InfoContent>
              </InfoItem>

              <div>
                <InfoTitle>Follow Us</InfoTitle>
                <SocialLinks>
                  <SocialLink href="https://www.facebook.com/otatofu/" target="_blank" rel="noopener noreferrer">
                    <FacebookIcon />
                  </SocialLink>
                  <SocialLink href="https://www.instagram.com/otatofu/" target="_blank" rel="noopener noreferrer">
                    <InstagramIcon />
                  </SocialLink>
                </SocialLinks>
              </div>
            </ContactInfo>
          </ContactGrid>

          <MapTitle>Find Us</MapTitle>
          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2795.5503045395485!2d-122.65993472372297!3d45.51913047107489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5495a0a6bc4d3957%3A0x4b1eced46512a7ee!2sOta%20Tofu!5e0!3m2!1sen!2sus!4v1744306612275!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>
        </Container>
      </Section>
    </div>
  );
};

export default Contact; 
 
 